import { useEffect, useMemo, useState } from "react";
import { createEmployeeComplaint, getEmployeeComplaints } from "../api/instateamComplaintsApi";
import { AppIcon } from "../components/InstateamEmployeeComponents";

const CATEGORY_OPTIONS = ["IT & Systems", "Facilities", "HR", "Payroll", "Documents", "Other"];
const PRIORITY_OPTIONS = ["low", "medium", "high"];

const INITIAL_FORM = {
  subject: "",
  category: "IT & Systems",
  priority: "medium",
  description: "",
};

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatLabel(value) {
  return String(value || "")
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export default function EmployeeComplaintsPage({ token, searchText = "" }) {
  const [complaints, setComplaints] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [activeTab, setActiveTab] = useState("list");
  const [form, setForm] = useState(INITIAL_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  async function fetchComplaints({ quiet = false } = {}) {
    if (!token) return;
    try {
      if (!quiet) setIsLoading(true);
      const payload = await getEmployeeComplaints(token);
      setComplaints(payload.complaints || []);
      setMetrics(payload.metrics || null);
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      if (!quiet) setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchComplaints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    const refreshComplaints = () => {
      if (!document.hidden) fetchComplaints({ quiet: true });
    };
    const intervalId = window.setInterval(refreshComplaints, 5000);
    window.addEventListener("focus", refreshComplaints);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshComplaints);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const visibleComplaints = useMemo(() => {
    const query = String(searchText || "").toLowerCase().trim();
    if (!query) return complaints;
    return complaints.filter((complaint) =>
      [
        complaint.complaintId,
        complaint.subject,
        complaint.category,
        complaint.priority,
        complaint.status,
        complaint.assignedTo,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [complaints, searchText]);

  const stats = useMemo(() => [
    { label: "Total Complaints", value: metrics?.total || 0, note: "All time", icon: "alertCircle", tone: "green" },
    { label: "Open", value: metrics?.open || 0, note: "Needs attention", icon: "chat", tone: "yellow" },
    { label: "In Progress", value: metrics?.inProgress || 0, note: "In progress", icon: "messageSquare", tone: "blue" },
    { label: "Resolved", value: metrics?.resolved || 0, note: "Completed", icon: "checkCircle", tone: "violet" },
    { label: "Closed", value: metrics?.closed || 0, note: "Closed", icon: "xCircle", tone: "red" },
  ], [metrics]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      setMessage({ type: "error", text: "Subject and description are required." });
      return;
    }

    try {
      setIsSaving(true);
      setMessage(null);
      await createEmployeeComplaint(token, form);
      setForm(INITIAL_FORM);
      setActiveTab("list");
      setMessage({ type: "success", text: "Complaint raised successfully." });
      await fetchComplaints({ quiet: true });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="employee-portal-page employee-complaints-page">
      <div className="employee-page-heading employee-complaint-heading-row">
        <div>
          <h1>My Complaints</h1>
          <div className="employee-breadcrumb">
            <span>Dashboard</span>
            <span>{">"}</span>
            <span>Complaints</span>
          </div>
        </div>
        <button type="button" className="employee-complaint-primary" onClick={() => setActiveTab("create")}>
          <AppIcon name="plus" />
          Raise New Complaint
        </button>
      </div>

      {message ? <div className={`employee-inline-alert ${message.type === "success" ? "success" : ""}`}>{message.text}</div> : null}

      <div className="employee-announcement-stats employee-complaint-stats">
        {stats.map((stat) => (
          <article key={stat.label} className="employee-stat-card employee-complaint-stat-card">
            <div className={`employee-large-icon ${stat.tone}`}>
              <AppIcon name={stat.icon} />
            </div>
            <div>
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
              <p>{stat.note}</p>
            </div>
          </article>
        ))}
      </div>

      <article className="employee-announcements-card employee-complaints-card">
        <div className="employee-complaint-tabs">
          <button type="button" className={activeTab === "list" ? "active" : ""} onClick={() => setActiveTab("list")}>
            My Complaints
          </button>
          <button type="button" className={activeTab === "create" ? "active" : ""} onClick={() => setActiveTab("create")}>
            Create New Complaint
          </button>
        </div>

        {activeTab === "create" ? (
          <form className="employee-complaint-form" onSubmit={handleSubmit}>
            <label>
              <span>Subject</span>
              <input value={form.subject} onChange={(event) => setForm((current) => ({ ...current, subject: event.target.value }))} placeholder="Enter complaint subject" />
            </label>
            <label>
              <span>Category</span>
              <select value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}>
                {CATEGORY_OPTIONS.map((category) => <option key={category} value={category}>{category}</option>)}
              </select>
            </label>
            <label>
              <span>Priority</span>
              <select value={form.priority} onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}>
                {PRIORITY_OPTIONS.map((priority) => <option key={priority} value={priority}>{formatLabel(priority)}</option>)}
              </select>
            </label>
            <label className="wide">
              <span>Description</span>
              <textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="Describe the issue clearly" />
            </label>
            <div className="employee-complaint-form-actions">
              <button type="button" className="employee-secondary-button" onClick={() => setActiveTab("list")}>Cancel</button>
              <button type="submit" className="employee-complaint-primary" disabled={isSaving}>
                {isSaving ? "Submitting..." : "Submit Complaint"}
              </button>
            </div>
          </form>
        ) : (
          <div className="employee-complaints-table">
            <div className="employee-complaints-header">
              <span>#</span>
              <span>Complaint ID</span>
              <span>Subject</span>
              <span>Category</span>
              <span>Priority</span>
              <span>Status</span>
              <span>Created On</span>
              <span>Actions</span>
            </div>
            {visibleComplaints.map((complaint, index) => (
              <div className="employee-complaints-row" key={complaint._id}>
                <span>{index + 1}</span>
                <span>{complaint.complaintId}</span>
                <strong>{complaint.subject}</strong>
                <span>{complaint.category}</span>
                <span><em className={`employee-priority-pill ${complaint.priority}`}>{formatLabel(complaint.priority)}</em></span>
                <span><em className={`employee-complaint-status ${complaint.status}`}>{formatLabel(complaint.status)}</em></span>
                <span>{formatDate(complaint.createdAt)}</span>
                <span className="employee-announcement-actions">
                  <button type="button" title="View complaint" onClick={() => window.alert(`${complaint.description || "No description"}${complaint.resolutionNote ? `\n\nResolution: ${complaint.resolutionNote}` : ""}`)}>
                    <AppIcon name="eye" />
                  </button>
                </span>
              </div>
            ))}
            {!visibleComplaints.length ? (
              <div className="employee-complaints-empty">
                {isLoading ? "Loading complaints..." : "No complaints found."}
              </div>
            ) : null}
          </div>
        )}
      </article>
    </section>
  );
}
